import { useMemo, useState } from "react";
import { api, formatApiError } from "../lib/api";
import { toast } from "sonner";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "./ui/dialog";
import { SunMedium, Search, Repeat2, Image as ImageIcon, Loader2 } from "lucide-react";

const eur = (n) => (n == null || n === "" ? "—" : Number(n).toLocaleString("it-IT", { style: "currency", currency: "EUR" }));
const dimsOf = (it) => { const d = (it && it.dimensioni_mm) || {}; return (d.larghezza && d.lunghezza) ? `${d.larghezza}×${d.lunghezza}` : null; };

// Selettore pannello dal LISTINO per il lead (fase iniziale, prima del layout). Solo CRM.
export function ListinoPanelPicker({ leadId, currentPanel, roof, onChanged, className = "" }) {
  const [open, setOpen] = useState(false);
  const [listino, setListino] = useState([]);
  const [loaded, setLoaded] = useState(false);
  const [q, setQ] = useState("");
  const [saving, setSaving] = useState(false);

  const layoutLocked = !!((roof?.panel_layout && roof.panel_layout.enabled) || (roof?.flat_layout && roof.flat_layout.compatible));
  const curL = currentPanel?.larghezza_mm, curH = currentPanel?.lunghezza_mm;

  const openDlg = () => {
    setOpen(true); setQ("");
    if (!loaded) api.get("/listino").then((r) => { setListino(r.data.items || []); setLoaded(true); }).catch((e) => toast.error(formatApiError(e)));
  };

  const moduli = useMemo(() => {
    let arr = listino.filter((it) => it.tipologia === "Moduli Fotovoltaici");
    if (layoutLocked && curL && curH) {
      arr = arr.filter((it) => { const d = it.dimensioni_mm || {}; return Number(d.larghezza) === Number(curL) && Number(d.lunghezza) === Number(curH); });
    }
    const s = q.trim().toLowerCase();
    if (s) arr = arr.filter((it) => [it.codice, it.descrizione, it.marca].some((v) => String(v || "").toLowerCase().includes(s)));
    return arr;
  }, [listino, q, layoutLocked, curL, curH]);

  const choose = async (codice) => {
    setSaving(true);
    try {
      const { data } = await api.post(`/leads/${leadId}/listino-panel`, { codice });
      toast.success("Pannello impianto aggiornato");
      onChanged?.(data);
      setOpen(false);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setSaving(false); }
  };

  return (
    <div className={`rounded-lg border border-primary/30 bg-primary/[0.04] p-3 flex items-center gap-3 ${className}`} data-testid="listino-panel-picker">
      <SunMedium className="h-5 w-5 text-primary shrink-0" strokeWidth={1.5} />
      <div className="min-w-0 flex-1">
        <div className="text-[10px] uppercase tracking-wide text-primary font-semibold">Pannello impianto</div>
        <div className="text-sm font-semibold truncate">
          {currentPanel?.codice || currentPanel?.model || "Catalogo (default)"}
          {currentPanel?.potenza_wp ? <span className="text-muted-foreground font-normal"> · {currentPanel.potenza_wp} Wp{(curL && curH) ? ` · ${curL}×${curH} mm` : ""}</span> : null}
        </div>
      </div>
      <Button variant="outline" size="sm" className="gap-1.5 shrink-0" onClick={openDlg} data-testid="listino-panel-change-btn"><Repeat2 className="h-4 w-4" /> {currentPanel?.codice ? "Cambia" : "Scegli"} pannello</Button>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-2xl">
          <DialogHeader><DialogTitle>Scegli il pannello dal listino</DialogTitle></DialogHeader>
          {layoutLocked && curL && curH ? (
            <div className="text-xs rounded-md border border-amber-400/50 bg-amber-50 text-amber-800 px-3 py-2">Layout già presente: puoi cambiare solo tra moduli con le <b>stesse dimensioni</b> ({curL}×{curH} mm), così non va rifatto il disegno.</div>
          ) : null}
          <div className="relative my-2">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Cerca modulo…" className="pl-9" data-testid="listino-panel-search" autoFocus />
          </div>
          <div className="max-h-[50vh] overflow-y-auto -mx-1 px-1 space-y-1.5">
            {!loaded ? <div className="py-8 text-center text-muted-foreground"><Loader2 className="h-5 w-5 animate-spin inline" /></div>
             : moduli.length === 0 ? <div className="py-8 text-center text-sm text-muted-foreground">Nessun modulo compatibile.</div>
             : moduli.map((it) => (
              <button key={it.codice} onClick={() => choose(it.codice)} disabled={saving} className="w-full flex items-center gap-3 rounded-lg border border-border hover:border-primary/50 hover:bg-secondary/40 p-2.5 text-left transition-colors disabled:opacity-50" data-testid={`listino-panel-option-${it.codice}`}>
                <div className="w-12 h-12 rounded overflow-hidden border border-border bg-secondary/30 shrink-0 flex items-center justify-center">{it.immagine_url ? <img src={it.immagine_url} alt="" className="w-full h-full object-cover" /> : <ImageIcon className="h-5 w-5 text-muted-foreground/40" />}</div>
                <div className="min-w-0 flex-1"><div className="text-xs font-mono font-semibold truncate">{it.codice}</div><div className="text-xs text-muted-foreground">{it.potenza_wp ? `${it.potenza_wp} Wp` : ""}{dimsOf(it) ? ` · ${dimsOf(it)} mm` : ""}</div></div>
                <div className="text-sm font-semibold text-primary shrink-0">{eur(it.prezzo_target)}</div>
              </button>
            ))}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
